import React from "react";
import { getBprHealth, getBprRedesign, extractBprResultRef } from "../services/bprRedesignService";

// Drives the BPR Redesign panel: an external, already-trained ML model with
// no config knobs and no background job — one POST, one result. Kept
// separate from useRlOptimizationData.js and useOptimizationData.js since
// the response shape (as_is/to_be full CMS process objects, heuristic_table
// with only 2 sections, cost_time_comparison) doesn't match either.
export default function useBprRedesignData() {
  // Reachability check only — never gates the Run button, since the model
  // being briefly unreachable shouldn't block a retry.
  const [healthy, setHealthy] = React.useState(null); // null = unknown

  const [result, setResult] = React.useState(null);
  const [running, setRunning] = React.useState(false);
  const [runError, setRunError] = React.useState("");
  // The run's identity, kept for the Save tab: `resultRef` is what
  // /save-optimized-version takes as ?bpr_result_ref to persist THIS result
  // rather than calling the external model again, and the sub-process flag has
  // to travel with it — a re-run under a mismatched flag is a different redesign.
  // The reference is valid for one hour (64 most recent results retained).
  const [runRef, setRunRef] = React.useState(null);

  const seqRef = React.useRef(0);

  const checkHealth = React.useCallback(async () => {
    try {
      await getBprHealth();
      setHealthy(true);
    } catch (_) {
      setHealthy(false);
    }
  }, []);

  // `options.includeSubprocessRedesigns` mirrors the RL panel's opt-in toggle — off
  // by default, since each sub-process costs an extra call to the external model.
  const run = React.useCallback(async (processId, options = {}) => {
    if (!processId) return;
    const seq = ++seqRef.current;
    setRunning(true);
    setRunError("");
    setResult(null);
    setRunRef(null);
    const includeSubprocessRedesigns = !!options.includeSubprocessRedesigns;
    try {
      const data = await getBprRedesign(processId, { includeSubprocessRedesigns });
      if (seq !== seqRef.current) return;
      setResult(data);
      const resultRef = extractBprResultRef(data);
      // A response without a result_ref predates the feature — offering BPR on the
      // Save tab then would mean composing from a re-run, so leave it unavailable.
      setRunRef(resultRef ? { processId, resultRef, includeSubprocessRedesigns } : null);
    } catch (e) {
      if (seq !== seqRef.current) return;
      setRunError(e?.message || "BPR redesign failed");
    } finally {
      if (seq === seqRef.current) setRunning(false);
    }
  }, []);

  const reset = React.useCallback(() => {
    seqRef.current += 1;
    setResult(null);
    setRunRef(null);
    setRunning(false);
    setRunError("");
  }, []);

  return {
    healthy,
    checkHealth,
    result,
    running,
    runError,
    run,
    runRef,
    reset,
  };
}
